import React, { useState, useEffect } from 'react';
import CommentDiv from './CommentDiv';
import CommentBox from './CommentBox';

export default function CommentLayer({ 
  comments, 
  pageNumber, 
  zoom, 
  activeComment,
  tempComment,
  onCommentClick,
  onCommentMove,
  onSaveComment,
  onDeleteComment,
  onCancelComment, // This is passed as onCancelComment from PdfContentArea
  containerRef
}) {
  const pageComments = comments.filter((c) => c.pageNumber === pageNumber);
  const [layerSize, setLayerSize] = useState({ width: 0, height: 0 });
  const layerRef = React.useRef(null);

  useEffect(() => {
    const updateLayerSize = () => {
      if (!layerRef.current) return;
      const r = layerRef.current.getBoundingClientRect();
      setLayerSize({ width: r.width, height: r.height });
    };
    
    updateLayerSize();
    window.addEventListener("resize", updateLayerSize);
    
    return () => window.removeEventListener("resize", updateLayerSize);
  }, [zoom]);

  // Calculate position for comment box with constraints
  const getCommentBoxPosition = (comment) => {
    if (!comment || !layerSize.width || !layerSize.height) {
      return { left: 100, top: 100 };
    }

    const pixelLeft = comment.normalizedLeft * layerSize.width;
    const pixelTop = comment.normalizedTop * layerSize.height;
    
    // Position the box to the right of the comment marker
    let left = pixelLeft + 30;
    let top = pixelTop;
    
    // If containerRef is provided, get its bounds for positioning
    let containerBounds = null;
    if (containerRef?.current) {
      const rect = containerRef.current.getBoundingClientRect();
      containerBounds = {
        left: rect.left,
        top: rect.top,
        right: rect.right,
        bottom: rect.bottom,
        width: rect.width,
        height: rect.height
      };
    }
    
    // Box dimensions (matching CommentBox.jsx)
    const boxWidth = 250;
    const boxHeight = 150;
    
    // Apply constraints if container bounds are available
    if (containerBounds) {
      // Convert to absolute viewport coordinates
      const absoluteLeft = containerBounds.left + left;
      const absoluteTop = containerBounds.top + top;
      
      // Check if box would go outside container on the right
      if (absoluteLeft + boxWidth > containerBounds.right - 10) {
        left = pixelLeft - boxWidth - 10;
      }
      
      // Check if box would go outside container on the bottom
      if (absoluteTop + boxHeight > containerBounds.bottom - 10) {
        top = Math.max(10, pixelTop - boxHeight + 20);
      }
      
      // Ensure box stays within container on the left
      if (absoluteLeft < containerBounds.left + 10) {
        left = 10;
      }
      
      // Ensure box stays within container on the top
      if (absoluteTop < containerBounds.top + 10) {
        top = 10;
      }
    } else {
      // Fallback: simple viewport constraints
      if (left + boxWidth > layerSize.width) {
        left = Math.max(10, pixelLeft - boxWidth - 10);
      }
      if (top + boxHeight > layerSize.height) {
        top = Math.max(10, layerSize.height - boxHeight - 10);
      }
    }
    
    return { left, top };
  };

  // Get the active comment object
  const getActiveComment = () => {
    if (tempComment) return tempComment;
    if (activeComment) {
      return pageComments.find(c => c.id === activeComment);
    }
    return null;
  };

  const activeCommentObj = getActiveComment();

  // Debug logging
  console.log('CommentLayer render:', {
    pageNumber,
    pageCommentsCount: pageComments.length,
    activeComment,
    tempComment,
    activeCommentObj,
    hasContainerRef: !!containerRef?.current,
    layerSize
  });

  return (
    <div
      ref={layerRef}
      className="comment-layer"
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: 'none',
        zIndex: 990,
      }}
    >
      {/* Render saved comments as markers */}
      {pageComments.map((comment) => (
        <CommentDiv
          key={comment.id}
          comment={comment}
          zoom={zoom}
          onCommentClick={onCommentClick}
          onCommentMove={onCommentMove}
          isActive={activeComment === comment.id}
        />
      ))}
      
      {/* Render comment box for active comment */}
      {activeCommentObj && (
        <CommentBox
          comment={activeCommentObj}
          onSave={onSaveComment}
          onDelete={onDeleteComment}
          onCancel={onCancelComment} // This is the key fix - pass as onCancel
          position={getCommentBoxPosition(activeCommentObj)}
          containerRef={containerRef}
        />
      )}
    </div>
  );
}